import { VAT_RATE } from 'src/config'
import {
  omsVatTreatmentImpliesHasVat,
  resolveLineVatTreatment,
} from 'src/utils/vatTreatment'

/** Catalogue lines: vatTreatment / product.hasVat fallback (Order.hasVat defaults false). */
const resolveLineHasVatForTotals = item => {
  if (item?.isSpecialOrder) return false
  return omsVatTreatmentImpliesHasVat(resolveLineVatTreatment(item))
}

/**
 * Matches UoM / pricedBy strings that denote weight-based pricing.
 * Kept in sync with server/src/utils/priceFunctions.js.
 * Deliberately conservative — matches whole tokens only so values like
 * "Green" or "Box" never false-positive. Cases: "kg", "KG", "per kg",
 * "kilo", "kilogram", "g", "gm", "gram", "lb", "lbs", "pound", "oz", "ounce".
 */
export const WEIGHT_UOM_PATTERN =
  /(^|[^a-z])(kgs?|kilos?|kilograms?|g|gms?|grams?|lbs?|pounds?|oz|ounces?)([^a-z]|$)/i

/**
 * Detects whether a product / order line / receipting item is priced by
 * actual delivered weight (catch-weight) rather than per case/unit.
 * Mirrors server-side isWeightPriced — same priority order:
 *   item.UoM → item.pricedBy → item.product.UoM → item.product.pricedBy
 * Returns false when nothing identifies the item as weight-priced — we
 * never infer it from quantity values.
 */
export const isWeightPriced = item => {
  if (!item || typeof item !== 'object') return false
  const candidates = [
    item.UoM,
    item.pricedBy,
    item.product?.UoM,
    item.product?.pricedBy,
  ]
  return candidates.some(
    value => typeof value === 'string' && WEIGHT_UOM_PATTERN.test(value),
  )
}

const calculatePrice = (price, hasVat) => {
  const productPriceIncExVAT = hasVat ? price * 100 * VAT_RATE : price * 100
  const val = Math.round(productPriceIncExVAT)
  return (val / 100).toFixed(2)
}

/** Special price in pounds when one is set (> 0), else null. */
export const getActiveSpecialPrice = venueProduct => {
  const raw = venueProduct?.specialPrice
  if (raw == null || raw === '' || isNaN(raw)) return null
  const price = Number(raw)
  return price > 0 ? price : null
}

/** Catalogue unit price for a venue product row (Brakes / special / supplier product). */
export const getVenueProductCatalogPrice = venueProduct => {
  if (!venueProduct) return 0
  if (
    venueProduct.isSpecialProduct &&
    venueProduct.specialPrice != null &&
    venueProduct.specialPrice !== '' &&
    !isNaN(venueProduct.specialPrice) &&
    Number(venueProduct.specialPrice) > 0
  ) {
    return Number(venueProduct.specialPrice)
  }
  if (
    venueProduct.brakesPrice != null &&
    venueProduct.brakesPrice !== '' &&
    !isNaN(venueProduct.brakesPrice) &&
    Number(venueProduct.brakesPrice) > 0
  ) {
    return Number(venueProduct.brakesPrice)
  }
  return venueProduct.product?.price ?? 0
}

/**
 * Brakes vs catalog unit price (express `resolveIngredientUnitPrice` parity).
 * Returns pounds per catalog pack unit for recipe line costing.
 *
 * 1. Prefer Brakes when finite and > 0 (already stored in pounds).
 * 2. Else catalog price from product snapshot (pence in DB → pounds here).
 */
export const normalizeBrakesUnitPrice = value => {
  if (value == null || value === '' || value === 'NaN') {
    return null
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/** Mongo catalog prices are pence unless formatted as a decimal string (Mongoose getter). */
export const parseCatalogUnitPrice = price => {
  if (price == null || price === '') return 0
  if (typeof price === 'string' && price.trim().includes('.')) {
    const num = parseFloat(price)
    return Number.isFinite(num) ? num : 0
  }
  const num = parseFloat(price)
  if (!Number.isFinite(num)) return 0
  // Raw pence (870, 869.999…) — float values fail Number.isInteger but are still pence.
  if (num >= 100) {
    return num / 100
  }
  return num
}

export const findVenueProductForIngredient = (
  ingredient,
  venueProducts = [],
) => {
  const productRef = ingredient?.product
  const productIdAsString =
    typeof productRef === 'string' || typeof productRef === 'number'
      ? String(productRef)
      : ''
  const venueProductId =
    productIdAsString ||
    (typeof productRef === 'object' ? productRef?.venueProductId : '') ||
    ingredient?.venueProductId ||
    ingredient?.productId
  const catalogProductId =
    typeof productRef === 'object' ? productRef?._id : undefined

  if (!venueProductId && !catalogProductId) {
    return null
  }

  return (
    venueProducts.find(
      venueProduct =>
        (venueProductId &&
          (String(venueProduct._id) === String(venueProductId) ||
            String(venueProduct.product?.venueProductId) ===
              String(venueProductId))) ||
        (catalogProductId &&
          String(venueProduct.product?._id) === String(catalogProductId)),
    ) || null
  )
}

export const resolveIngredientUnitPrice = ({
  ingredient,
  venueProduct,
  product,
} = {}) => {
  const brakesPrice = normalizeBrakesUnitPrice(
    ingredient?.brakesPrice ??
      venueProduct?.brakesPrice ??
      product?.brakesPrice,
  )
  if (brakesPrice != null) {
    return brakesPrice
  }

  const catalogSource =
    product?.price ??
    ingredient?.product?.price ??
    venueProduct?.product?.price ??
    ingredient?.price

  return parseCatalogUnitPrice(catalogSource)
}

export const resolveVenueProductUnitPrice = venueProduct =>
  resolveIngredientUnitPrice({
    venueProduct,
    product: venueProduct?.product,
  })

/**
 * CustomProduct.price is pence in MongoDB; getter returns pounds ("10.00").
 * Use for recipe custom-ingredient line costing (express parity).
 */
export const parseCustomProductUnitPrice = price => {
  if (price == null || price === '') return 0

  if (typeof price === 'string') {
    const trimmed = price.trim()
    const num = parseFloat(trimmed)
    if (!Number.isFinite(num)) return 0
    if (trimmed.includes('.')) {
      return num
    }
    return num / 100
  }

  const num = Number(price)
  if (!Number.isFinite(num)) return 0

  if (Number.isInteger(num)) {
    return num / 100
  }

  return num
}

/** Pence integer for API save (CustomProduct stores pence). */
export const formatCustomProductPriceForSave = pounds => {
  const num = Number(pounds)
  if (!Number.isFinite(num)) return 0
  return Math.round(num * 100)
}

/** Pounds for form display/edit — prefers explicit form state over persisted pence. */
export const getCustomProductPricePounds = item => {
  if (item?.pricePounds != null && item.pricePounds !== '') {
    const parsed = Number(item.pricePounds)
    return Number.isFinite(parsed) ? parsed : 0
  }
  if (item?.product?.price != null && item.product.price !== '') {
    return parseCustomProductUnitPrice(item.product.price)
  }
  if (item?.price != null && item.price !== '') {
    const parsed = Number(item.price)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export const mapCustomProductsForSave = (customProducts = []) =>
  customProducts.map(item => {
    const pounds = getCustomProductPricePounds(item)
    const pricePounds = Number.isFinite(Number(pounds)) ? Number(pounds) : 0
    const priceString = pricePounds.toFixed(2)
    if (item?.product) {
      return {
        ...item,
        pricePounds,
        price: priceString,
        product: {
          ...item.product,
          pricePounds,
          price: priceString,
        },
      }
    }
    return {
      ...item,
      pricePounds,
      price: priceString,
    }
  })

/**
 * Some basket writes (e.g. Order-to-Par lean catalogue pence through the
 * Order setter) stored order.price 100× too high vs product.price.
 * Correct only that clear mismatch for display/totals — does not write to DB.
 */
export const correctHundredXPriceMismatch = (orderPrice, productPrice) => {
  const orderNum = Number(orderPrice)
  const productNum = Number(productPrice)
  if (
    !Number.isFinite(orderNum) ||
    !Number.isFinite(productNum) ||
    productNum <= 0 ||
    Math.round(orderNum / productNum) !== 100
  ) {
    return orderPrice
  }
  return orderNum / 100
}

export const getPrice = item => {
  let price =
    item?.isSpecialOrder && !isNaN(item?.specialProductPrice)
      ? item?.specialProductPrice
      : item.receiptingStatus === 'Rejected by supplier' &&
        !isNaN(item?.product?.price) &&
        item?.product?.price >= 0 &&
        item.product?.price != null &&
        item.product?.price !== undefined &&
        item.product?.price != 'NaN'
      ? item.product?.price
      : item?.receipting?.updatedPrice != null &&
        !isNaN(item?.receipting?.updatedPrice) &&
        item?.receipting?.updatedPrice >= 0
      ? item?.receipting?.updatedPrice / 100
      : item?.brakesPrice && Number(item.brakesPrice) > 0
      ? correctHundredXPriceMismatch(item.brakesPrice, item.product?.price)
      : !isNaN(item?.price) &&
        item?.price >= 0 &&
        item.price !== undefined &&
        item.price != 'NaN' &&
        item.price != null
      ? correctHundredXPriceMismatch(item.price, item.product?.price)
      : !isNaN(item?.product?.price)
      ? item?.product?.price
      : 0
  return price
}
export const getOMSPrice = item => {
  const specialProdprice = item?.isSpecialOrder ? item?.OMSSpecialPrice : ''
  const price =
    item.OMSPrice !== undefined && !isNaN(item.OMSPrice)
      ? item.OMSPrice
      : item.product.OMSPrice !== undefined
      ? item.product.OMSPrice
      : 0

  let adminReceiptingPrice
  if (
    item?.receipting &&
    item.receipting?.error === 'Adjustment due to Invoice received' &&
    item?.receipting?.updatedPrice >= 0
  ) {
    adminReceiptingPrice = item.receipting?.updatedPrice / 100
  } else {
    adminReceiptingPrice = price
  }
  return calculatePrice(
    adminReceiptingPrice !== price
      ? adminReceiptingPrice
      : specialProdprice
      ? specialProdprice
      : adminReceiptingPrice,
    false,
  )
}

const calculatePriceWithQuantity = (
  price,
  hasVat,
  quantity,
  updatedQuantity,
) => {
  const productPriceIncExVAT = hasVat ? price * 100 * VAT_RATE : price * 100
  let val = productPriceIncExVAT * (updatedQuantity || quantity)
  val = Math.round(val)
  return (val / 100).toFixed(2)
}

export const getOrderPrice = item => {
  if (item?.isSpecialOrder && isNaN(item?.specialProductPrice)) {
    return item?.specialProductPrice
  }
  if (item.receiptingStatus === 'Rejected by supplier') {
    return item.product?.price || 0
  }
  const price =
    item?.brakesPrice && Number(item.brakesPrice) > 0
      ? correctHundredXPriceMismatch(item.brakesPrice, item.product?.price)
      : item.price !== undefined && !isNaN(item.price)
      ? correctHundredXPriceMismatch(item.price, item.product?.price)
      : item.product.price !== undefined
      ? item.product.price
      : 0
  return price
}

/** Unit price for multi-venue orders report (Brakes-aware; prefers catalogue over stale order.price). */
export const getVenueOrderUnitPrice = order => {
  if (!order) return 0
  if (
    order.isSpecialOrder &&
    order.specialProductPrice != null &&
    !isNaN(order.specialProductPrice)
  ) {
    return Number(order.specialProductPrice)
  }
  if (
    order.brakesPrice != null &&
    !isNaN(order.brakesPrice) &&
    Number(order.brakesPrice) > 0
  ) {
    return Number(order.brakesPrice)
  }
  const productPrice = order.product?.price
  if (
    productPrice != null &&
    productPrice !== '' &&
    productPrice !== 'NaN' &&
    !isNaN(productPrice)
  ) {
    return Number(productPrice)
  }
  if (order.price != null && order.price !== 'NaN' && !isNaN(order.price)) {
    return Number(order.price)
  }
  if (
    order.OMSPrice != null &&
    order.OMSPrice !== 'NaN' &&
    !isNaN(order.OMSPrice)
  ) {
    return Number(order.OMSPrice)
  }
  if (
    order.OMSSpecialPrice != null &&
    order.OMSSpecialPrice !== 'NaN' &&
    !isNaN(order.OMSSpecialPrice)
  ) {
    return Number(order.OMSSpecialPrice)
  }
  return 0
}

/** productId → catalogue unit price (pounds) for Venue Report CSV. */
export const buildVenueProductCatalogPriceMap = venueProducts => {
  const map = new Map()
  for (const vp of venueProducts || []) {
    const id = vp?.product?._id ?? vp?.product
    if (id == null) continue
    map.set(String(id), getVenueProductCatalogPrice(vp))
  }
  return map
}

/** Order line unit price in pounds (Brakes catalogue via venueProducts map, else aggregated product price). */
export const getVenueReportOrderLineUnitPrice = (
  currentProduct,
  catalogPriceByProductId,
) => {
  const productId = currentProduct?._id
  if (productId != null && catalogPriceByProductId) {
    const catalog = catalogPriceByProductId.get(String(productId))
    if (catalog != null && !isNaN(catalog) && Number(catalog) > 0) {
      return Number(catalog)
    }
  }
  const raw = currentProduct?.price
  if (raw == null || raw === '' || isNaN(raw)) return 0
  const n = Number(raw)
  return n >= 100 ? n / 100 : n
}

export const getOrderedQuantity = item => {
  const fromReceipting = Number(item?.receipting?.orderedQuantity)
  if (Number.isFinite(fromReceipting)) {
    return fromReceipting
  }
  const fromOrdered = Number(item?.orderedQuantity)
  if (Number.isFinite(fromOrdered)) {
    return fromOrdered
  }
  const fromQuantity = Number(item?.quantity)
  return Number.isFinite(fromQuantity) ? fromQuantity : 0
}

const parseFiniteQuantity = value => {
  if (value === null || value === undefined || value === '') {
    return NaN
  }
  const num = Number(value)
  return Number.isFinite(num) ? num : NaN
}

const getPopulatedReceipting = item => {
  const receipting = item?.receipting
  if (
    !receipting ||
    typeof receipting !== 'object' ||
    Array.isArray(receipting)
  ) {
    return null
  }
  const keys = Object.keys(receipting)
  if (keys.length === 1 && keys[0] === '_id') {
    return null
  }
  return receipting
}

/**
 * Billable (pricing) weight captured on a populated receipting doc for a
 * catch-weight product. Returns NaN when absent — callers fall through.
 * Only positive weights count: a 0/absent weight means "not entered yet",
 * so the case quantity must stay authoritative until it is known.
 * Kept in sync with server/src/utils/priceFunctions.js.
 */
const getBillableWeight = item => {
  if (!isWeightPriced(item)) return NaN
  const receipting = getPopulatedReceipting(item)
  if (!receipting) return NaN
  const weight = parseFiniteQuantity(receipting.weight ?? item?.weight)
  return !Number.isNaN(weight) && weight > 0 ? weight : NaN
}

/**
 * Effective received quantity for GR totals, Match, GP, and spend.
 *
 * Catch-weight products (catalogue UoM priced per kg etc.): the billable
 * weight IS the pricing quantity, so it wins over the received case count
 * (ordered 2 cases, billed 6.94 kg => value uses 6.94). Legacy
 * 'Weighted item' receipts keep working even when the catalogue gives no
 * UOM to detect from.
 */
export const getQuantity = item => {
  const billableWeight = getBillableWeight(item)
  if (!Number.isNaN(billableWeight)) {
    return billableWeight
  }

  const topLevelQty = parseFiniteQuantity(item?.updatedQuantity)
  if (!Number.isNaN(topLevelQty)) {
    return topLevelQty
  }

  const receipting = getPopulatedReceipting(item)
  if (receipting) {
    const receiptingQty = parseFiniteQuantity(receipting.updatedQuantity)
    if (!Number.isNaN(receiptingQty)) {
      return receiptingQty
    }

    if (receipting.error === 'Weighted item') {
      const weight = parseFiniteQuantity(receipting.weight ?? item?.weight)
      if (!Number.isNaN(weight)) {
        return weight
      }
    }

    const previousQty = parseFiniteQuantity(receipting.previousQty)
    if (
      !Number.isNaN(previousQty) &&
      receipting.error === 'Supplier / Invoice adjustment'
    ) {
      return previousQty
    }
  }

  const orderQty = parseFiniteQuantity(item?.quantity)
  if (!Number.isNaN(orderQty)) {
    return orderQty
  }

  return 0
}
export const getOrderPriceNoVAT = item => {
  let price = 0
  if (item?.brakesPrice && Number(item.brakesPrice) > 0) {
    price = item.brakesPrice
  }

  if (isNaN(item?.product?.price ?? item.price)) {
    price = 0
  } else if (item?.product?.price) {
    price = item?.product?.price === undefined ? 0 : item?.product?.price
  } else {
    price = item.price === undefined ? 0 : item.price
  }

  const { updatedQuantity, quantity } = item

  return calculatePriceWithQuantity(price, false, quantity, updatedQuantity)
}

export const getItemReceiptingPrice = item => {
  let price = getPrice(item)
  const hasVat = resolveLineHasVatForTotals(item)
  let quantity = getQuantity(item)
  // Catch-weight: the billable weight already came back from getQuantity,
  // so the case count must not override it in calculatePriceWithQuantity.
  const updatedQuantity = !Number.isNaN(getBillableWeight(item))
    ? null
    : item?.receipting?.updatedQuantity
  return calculatePriceWithQuantity(price, hasVat, quantity, updatedQuantity)
}

export const getPriceWithVat = item => {
  const price = getPrice(item)
  return calculatePrice(price, resolveLineHasVatForTotals(item))
}

export const getTotalPrice = array =>
  array
    ? array.reduce(
        (accumulator, item) =>
          Number(
            parseFloat(accumulator) + parseFloat(getOrderPrice(item)),
          ).toFixed(2),
        0,
      )
    : 0

export const getTotalPriceNoVAT = array =>
  array
    ? array.reduce(
        (accumulator, item) =>
          Number(
            parseFloat(accumulator) + parseFloat(getOrderPriceNoVAT(item)),
          ).toFixed(2),
        0,
      )
    : 0

export const getTotalOMSPrice = array =>
  array
    ? array.reduce(
        (accumulator, item) => {
          const specialProdprice = item?.isSpecialOrder
            ? item?.OMSSpecialPrice
            : ''
          const price =
            item.OMSPrice !== undefined && !isNaN(item.OMSPrice)
              ? item.OMSPrice
              : item.product.OMSPrice !== undefined
              ? item.product.OMSPrice
              : 0

          // const adminReceiptingPrice =
          //     (item?.receipting?.error === 'Adjustment due to Invoice received' &&
          //         item?.receipting?.updatedPrice / 100) ||
          //     specialProdprice ||
          //     price;

          let adminReceiptingPrice

          if (
            item?.receipting?.error === 'Adjustment due to Invoice received' &&
            item?.receipting?.updatedPrice >= 0
          ) {
            adminReceiptingPrice = item?.receipting?.updatedPrice / 100
          } else {
            adminReceiptingPrice = specialProdprice || price
          }

          const hasVat = resolveLineHasVatForTotals(item)
          const { updatedQuantity, quantity, supplierConfirmed, receipting } =
            item

          if (!supplierConfirmed) {
            return accumulator
          }

          // const adminReceiptingQuantity =
          //     (receipting?.error === 'Adjustment due to Invoice received' &&
          //         (item.UoM === 'Kg' && receipting?.weight
          //             ? receipting.weight
          //             : receipting?.updatedQuantity)) ||
          //     updatedQuantity ||
          //     quantity;

          let adminReceiptingQuantity

          if (receipting?.error === 'Adjustment due to Invoice received') {
            if (item.UoM === 'Kg' && receipting?.weight >= 0) {
              adminReceiptingQuantity = receipting?.weight
            } else if (receipting?.updatedQuantity >= 0) {
              adminReceiptingQuantity = receipting?.updatedQuantity
            } else {
              adminReceiptingQuantity = updatedQuantity || quantity
            }
          } else {
            adminReceiptingQuantity = updatedQuantity || quantity
          }

          const total = parseFloat(
            adminReceiptingQuantity * adminReceiptingPrice,
          )
          accumulator.total += total

          if (hasVat) {
            const vat = parseFloat(total * 0.2)
            accumulator.VAT += vat
            accumulator.totalVat += total + vat
          } else {
            accumulator.totalVat += total
          }

          return accumulator
        },
        { total: 0, VAT: 0, totalVat: 0 },
      )
    : { total: 0, VAT: 0, totalVat: 0 }

export const getTotalSpendReceipting = array => {
  const filterdOrders = array.filter(
    order =>
      order.supplierConfirmed === undefined || order.supplierConfirmed === true,
  )
  const totalPrice = filterdOrders?.reduce((sum, item) => {
    let quantity = getQuantity(item)
    let price = getPrice(item)
    if (isNaN(price)) price = 0
    if (isNaN(quantity)) quantity = 0
    const lineTotal = price * quantity

    return sum + lineTotal
  }, 0)
  return totalPrice
}

export const getTotalSpendReceiptingWithVAT = array => {
  const filterdOrders = array.filter(
    order =>
      order.supplierConfirmed === undefined || order.supplierConfirmed === true,
  )
  let totalSpend = 0
  let totalVAT = 0

  filterdOrders.forEach(item => {
    let quantity = getQuantity(item)
    let price = getPrice(item)
    if (isNaN(price)) price = 0
    if (isNaN(quantity)) quantity = 0

    const lineTotal = price * quantity
    const hasVat = resolveLineHasVatForTotals(item)

    const vatAmount = hasVat ? lineTotal * (VAT_RATE - 1) : 0

    totalSpend += lineTotal + vatAmount
    totalVAT += vatAmount
  })

  return {
    totalSpend: Number(totalSpend.toFixed(2)),
    totalVAT: Number(totalVAT.toFixed(2)),
  }
}

export const getTotalSpendOrderedWithVAT = array => {
  // Include all items regardless of confirmation status to show original ordered amount
  const total = array.reduce((total, item) => {
    const currentPrice = getOrderPrice(item)

    if (isNaN(currentPrice)) {
      console.warn('Invalid price for item:', item)
      return total
    }

    let sum = 0

    if (item.receiptingStatus === 'Rejected by supplier') {
      sum = item.product?.price * getOrderedQuantity(item)
    } else {
      sum = currentPrice * getOrderedQuantity(item)
    }

    const hasVat = resolveLineHasVatForTotals(item)

    const vatAmount = hasVat ? sum * (VAT_RATE - 1) : 0

    return total + sum + vatAmount
  }, 0)
  return Number(total.toFixed(2))
}

export const getTotalSpendOrderedNoVAT = array => {
  const filterdOrders = array.filter(
    order =>
      order.supplierConfirmed === undefined || order.supplierConfirmed === true,
  )
  return filterdOrders.reduce((total, item) => {
    const currentPrice = getOrderPrice(item)

    if (isNaN(currentPrice)) {
      console.warn('Invalid price for item:', item)
      return total
    }

    let sum = 0
    if (item.receiptingStatus === 'Rejected by supplier') {
      sum = item.product?.price * getOrderedQuantity(item)
    } else {
      sum = currentPrice * getOrderedQuantity(item)
    }
    return total + sum
  }, 0)
}

// Utility function to calculate total price based on size change (pack pro-rata).
export const calculateTotalPrice = (originalSize, originalPrice, newSize) => {
  return computeIngredientLineCost({
    productSize: originalSize,
    unitPrice: originalPrice,
    selectedSize: newSize,
  }).lineTotal
}

const SPACED_MULTI_PACK_PATTERN = /\d+(?:\.\d+)?\s+[xX]\s+\d/

/** Pack divisor for pro-rata — express `resolvePackSizeFromProductSize` parity. */
export const resolvePackSizeFromProductSize = (productSize = '') => {
  const sizeStr = String(productSize || '').trim()
  const { numbers, totalSize } = extractAndCalculateSize(sizeStr)

  if (numbers.length === 0) {
    return 1
  }

  if (numbers.length === 1) {
    return numbers[0] || 1
  }

  if (SPACED_MULTI_PACK_PATTERN.test(sizeStr)) {
    return numbers[0] || 1
  }

  return totalSize > 0 ? totalSize : numbers[0] || 1
}

/**
 * OMS ingredient line cost — express `calculateIngredientLineTotal` parity.
 */
export const computeIngredientLineCost = ({
  productSize = '',
  unitPrice = 0,
  selectedSize = 0,
  UoM = '',
  conversion = null,
} = {}) => {
  const size = Number(selectedSize) || 0
  const price = Number(unitPrice) || 0

  if (size <= 0 || price <= 0) {
    return { lineTotal: 0, sizeLabel: 'N/A', selectedSize: size }
  }

  if (conversion?.toUnit && conversion?.toQty) {
    const converted = convertUnitPrice({
      actualUnit: conversion.toUnit,
      actualPrice: price,
      actualFactor: conversion.toQty,
      newUnit: UoM,
      newFactor: size,
    })

    if (converted != null && Number.isFinite(Number(converted))) {
      return {
        lineTotal: converted || 0,
        sizeLabel: `${conversion.toQty} ${conversion.toUnit}`,
        selectedSize: size,
      }
    }
    // Fall through to pack pro-rata when units are unknown/mismatched.
  }

  const packSize = resolvePackSizeFromProductSize(productSize)
  const lineTotal = (price / packSize) * size
  const sizeLabel =
    typeof productSize === 'string' && productSize.trim() !== ''
      ? productSize
      : `${productSize} ${UoM || ''}`.trim()

  return {
    lineTotal,
    sizeLabel,
    selectedSize: size,
  }
}

export const computeCustomIngredientLineCost = (customIngredient = {}) => {
  const size = Number(customIngredient?.size) || 0
  const unitPrice = parseCustomProductUnitPrice(
    customIngredient?.product?.price ?? customIngredient?.price,
  )
  return {
    lineTotal: size > 0 && unitPrice > 0 ? unitPrice * size : 0,
    sizeLabel: customIngredient?.product?.UoM || customIngredient?.UoM || '',
    selectedSize: size,
  }
}

export const extractAndCalculateSize = input => {
  if (!input || typeof input !== 'string') {
    return { numbers: [], uom: null, totalSize: 0 }
  }

  if (!Number.isNaN(Number(input.trim())) && input.trim() !== '') {
    return { numbers: [Number(input)], uom: null, totalSize: Number(input) }
  }

  const numbers = input.match(/(\d+(?:\.\d+)?)/g)?.map(Number) || []
  const uom = input.replace(/[\d.\sxX]/g, '').trim() || null
  const totalSize =
    numbers.length > 0 ? numbers.reduce((acc, num) => acc * num, 1) : 0

  return { numbers, uom, totalSize }
}

export const calculateTotalPriceFromConversion = (
  conversion,
  price,
  conversionFactors,
  toUnitType,
) => {
  // Extract the necessary details from the conversion object
  const { toQty, toUnit } = conversion

  // Check if the unit type is valid (weight, volume, count)
  if (
    !conversionFactors[toUnitType] ||
    !conversionFactors[toUnitType][toUnit]
  ) {
    console.error('Invalid unit or conversion factor')
    return 0 // Return 0 or any other default value if the conversion is invalid
  }

  // Calculate the size based on the selected quantity and conversion factor
  const size = toQty * (conversionFactors[toUnitType][toUnit] || 1)

  // Calculate and return the total price based on the calculated size
  const totalPrice = (price / toQty) * size

  return totalPrice
}

export const conversionFactors = {
  weight: {
    kg: 1,
    g: 1000,
    mg: 1000000,
    lb: 2.20462,
    oz: 35.274,
  },
  volume: {
    ltr: 1,
    ml: 1000,
    cl: 100,
    gal: 0.264172,
    floz: 33.814,
  },
  count: {
    unit: 1,
    piece: 1,
    item: 1,
    pack: 1,
    box: 1,
  },
}

const unitToCategoryMap = {
  kg: 'weight',
  g: 'weight',
  mg: 'weight',
  lb: 'weight',
  oz: 'weight',
  ltr: 'volume',
  ml: 'volume',
  cl: 'volume',
  gal: 'volume',
  floz: 'volume',
  unit: 'count',
  piece: 'count',
  item: 'count',
  pack: 'count',
  box: 'count',
}

/** Map common labels / aliases to canonical unit keys used in conversionFactors. */
const unitAliases = {
  gram: 'g',
  grams: 'g',
  kilogram: 'kg',
  kilograms: 'kg',
  milligram: 'mg',
  milligrams: 'mg',
  pound: 'lb',
  pounds: 'lb',
  ounce: 'oz',
  ounces: 'oz',
  litre: 'ltr',
  litres: 'ltr',
  liter: 'ltr',
  liters: 'ltr',
  l: 'ltr',
  millilitre: 'ml',
  millilitres: 'ml',
  milliliter: 'ml',
  milliliters: 'ml',
  centilitre: 'cl',
  centilitres: 'cl',
  centiliter: 'cl',
  centiliters: 'cl',
  gallon: 'gal',
  gallons: 'gal',
  'fluid ounce': 'floz',
  'fluid ounces': 'floz',
  fl: 'floz',
  pieces: 'piece',
  units: 'unit',
  items: 'item',
  packs: 'pack',
  boxes: 'box',
}

export const normalizeUnit = unit => {
  if (unit == null || unit === '') return null
  const lower = String(unit).trim().toLowerCase()
  if (!lower) return null
  return unitAliases[lower] || lower
}

export const getConversionCategory = unit => {
  const normalized = normalizeUnit(unit)
  if (!normalized) return null
  return unitToCategoryMap[normalized] || null
}

export const convertUnitPrice = ({
  actualUnit,
  actualPrice,
  actualFactor,
  newUnit,
  newFactor,
}) => {
  const normalizedActual = normalizeUnit(actualUnit)
  const normalizedNew = normalizeUnit(newUnit)
  const category = getConversionCategory(normalizedActual)
  const newCategory = getConversionCategory(normalizedNew)

  // Soft-fail: unknown/mismatched units must not crash recipe edit/details UI.
  if (
    !normalizedActual ||
    !normalizedNew ||
    !category ||
    !newCategory ||
    category !== newCategory
  ) {
    return null
  }

  const factors = conversionFactors[category]
  const actualUnitFactor = factors?.[normalizedActual]
  const newUnitFactor = factors?.[normalizedNew]
  if (!actualUnitFactor || !newUnitFactor) {
    return null
  }

  const pricePerBase = actualPrice / (actualFactor / actualUnitFactor)
  return pricePerBase * (newFactor / newUnitFactor)
}
